/**
 * useAdminDashboard Hook
 * TanStack Query hooks for WeBeMail Admin Dashboard
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EdgeMailAdminAPI } from '../services/EdgeMailAdminAPI';
import type { AlertSeverity, AlertCategory } from '../types/admin.types';

// Query keys
export const adminQueryKeys = {
  all: ['webemail-admin'] as const,
  dashboard: () => [...adminQueryKeys.all, 'dashboard'] as const,
  metrics: () => [...adminQueryKeys.dashboard(), 'metrics'] as const,
  health: () => [...adminQueryKeys.dashboard(), 'health'] as const,
  alerts: (filters?: { severity?: AlertSeverity; category?: AlertCategory }) =>
    [...adminQueryKeys.dashboard(), 'alerts', filters] as const,
  activity: (days?: number) => [...adminQueryKeys.dashboard(), 'activity', days] as const,
  topStorageUsers: (limit?: number) =>
    [...adminQueryKeys.dashboard(), 'top-storage-users', limit] as const,
  tenantConfig: () => [...adminQueryKeys.all, 'tenant-config'] as const,
  inboundQueueHealth: () => [...adminQueryKeys.dashboard(), 'inbound-queue-health'] as const,
  outboundQueueHealth: () => [...adminQueryKeys.dashboard(), 'outbound-queue-health'] as const,
  outboundQueueMetrics: (domain?: string) => [...adminQueryKeys.dashboard(), 'outbound-queue-metrics', domain] as const,
  connectedClients: () => [...adminQueryKeys.dashboard(), 'connected-clients'] as const,
  evidenceEmbeddings: () => [...adminQueryKeys.all, 'evidence-embeddings'] as const,
  evidenceEmbeddingPolicy: (mailboxId?: string) =>
    [...adminQueryKeys.evidenceEmbeddings(), 'policy', mailboxId || 'tenant'] as const,
  evidenceEmbeddingSchedules: () => [...adminQueryKeys.evidenceEmbeddings(), 'schedules'] as const,
  evidenceEmbeddingRuns: (limit?: number) =>
    [...adminQueryKeys.evidenceEmbeddings(), 'runs', limit || 50] as const,
};

/**
 * Hook to fetch dashboard metrics
 */
export function useDashboardMetrics() {
  return useQuery({
    queryKey: adminQueryKeys.metrics(),
    queryFn: () => EdgeMailAdminAPI.getDashboardMetrics(),
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 10000,
  });
}

/**
 * Hook to fetch system health
 */
export function useSystemHealth() {
  return useQuery({
    queryKey: adminQueryKeys.health(),
    queryFn: () => EdgeMailAdminAPI.getSystemHealth(),
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000,
  });
}

/**
 * Hook to fetch alerts
 */
export function useAlerts(options?: {
  severity?: AlertSeverity;
  category?: AlertCategory;
  includeResolved?: boolean;
  limit?: number;
}) {
  return useQuery({
    queryKey: adminQueryKeys.alerts({ severity: options?.severity, category: options?.category }),
    queryFn: () => EdgeMailAdminAPI.getAlerts(options),
    refetchInterval: 30000,
    staleTime: 10000,
  });
}

/**
 * Hook to acknowledge an alert
 */
export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alertId: string) => EdgeMailAdminAPI.acknowledgeAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.alerts() });
    },
  });
}

/**
 * Hook to fetch admin activity summary
 */
export function useAdminActivitySummary(options?: { days?: number; limit?: number }) {
  return useQuery({
    queryKey: adminQueryKeys.activity(options?.days),
    queryFn: () => EdgeMailAdminAPI.getAdminActivitySummary(options),
    staleTime: 30000,
  });
}

/**
 * Hook to fetch top storage users
 */
export function useTopStorageUsers(limit?: number) {
  return useQuery({
    queryKey: adminQueryKeys.topStorageUsers(limit),
    queryFn: () => EdgeMailAdminAPI.getTopStorageUsers(limit),
    staleTime: 60000,
  });
}

/**
 * Hook to fetch tenant configuration
 */
export function useTenantConfig() {
  return useQuery({
    queryKey: adminQueryKeys.tenantConfig(),
    queryFn: () => EdgeMailAdminAPI.getTenantConfig(),
    staleTime: 300000, // 5 minutes
  });
}

/**
 * Hook to update tenant configuration
 */
export function useUpdateTenantConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: Record<string, unknown>) => EdgeMailAdminAPI.updateTenantConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.tenantConfig() });
    },
  });
}

/**
 * Hook to sync Keycloak users
 */
export function useSyncKeycloakUsers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => EdgeMailAdminAPI.syncKeycloakUsers(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.metrics() });
    },
  });
}

/**
 * Hook to clear cache
 */
export function useClearCache() {
  return useMutation({
    mutationFn: () => EdgeMailAdminAPI.clearCache(),
  });
}

/**
 * Hook to fetch inbound queue health
 */
export function useInboundQueueHealth() {
  return useQuery({
    queryKey: adminQueryKeys.inboundQueueHealth(),
    queryFn: () => EdgeMailAdminAPI.getInboundQueueHealth(),
    refetchInterval: 15000, // Refresh every 15 seconds
    staleTime: 5000,
  });
}

/**
 * Hook to fetch outbound queue health
 */
export function useOutboundQueueHealth() {
  return useQuery({
    queryKey: adminQueryKeys.outboundQueueHealth(),
    queryFn: () => EdgeMailAdminAPI.getOutboundQueueHealth(),
    refetchInterval: 15000,
    staleTime: 5000,
  });
}

/**
 * Hook to fetch connected clients
 */
export function useConnectedClients() {
  return useQuery({
    queryKey: adminQueryKeys.connectedClients(),
    queryFn: () => EdgeMailAdminAPI.getConnectedClients(),
    refetchInterval: 30000,
    staleTime: 15000,
  });
}

export function useEvidenceEmbeddingPolicy(mailboxId?: string) {
  return useQuery({
    queryKey: adminQueryKeys.evidenceEmbeddingPolicy(mailboxId),
    queryFn: () => EdgeMailAdminAPI.getEvidenceEmbeddingPolicy(mailboxId),
    staleTime: 30000,
  });
}

export function useUpdateEvidenceEmbeddingPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: EdgeMailAdminAPI.updateEvidenceEmbeddingPolicy,
    onSuccess: (_, input) => {
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.evidenceEmbeddingPolicy(input.mailboxId || undefined),
      });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.evidenceEmbeddingSchedules() });
    },
  });
}

export function useEvidenceEmbeddingSchedules() {
  return useQuery({
    queryKey: adminQueryKeys.evidenceEmbeddingSchedules(),
    queryFn: () => EdgeMailAdminAPI.listEvidenceEmbeddingSchedules(),
    refetchInterval: 30000,
    staleTime: 10000,
  });
}

export function useCreateEvidenceEmbeddingSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: EdgeMailAdminAPI.createEvidenceEmbeddingSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.evidenceEmbeddingSchedules() });
    },
  });
}

export function useCancelEvidenceEmbeddingSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: EdgeMailAdminAPI.cancelEvidenceEmbeddingSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.evidenceEmbeddingSchedules() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.evidenceEmbeddingRuns() });
    },
  });
}

export function usePreviewEvidenceEmbeddingRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: EdgeMailAdminAPI.previewEvidenceEmbeddingRun,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.evidenceEmbeddingRuns() });
    },
  });
}

export function useCancelEvidenceEmbeddingRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: EdgeMailAdminAPI.cancelEvidenceEmbeddingRun,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.evidenceEmbeddingRuns() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.evidenceEmbeddingSchedules() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.inboundQueueHealth() });
    },
  });
}

export function useEvidenceEmbeddingRuns(limit = 50) {
  return useQuery({
    queryKey: adminQueryKeys.evidenceEmbeddingRuns(limit),
    queryFn: () => EdgeMailAdminAPI.listEvidenceEmbeddingRuns(limit),
    refetchInterval: 30000,
    staleTime: 10000,
  });
}

export function useStartEvidenceEmbeddingRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: EdgeMailAdminAPI.startEvidenceEmbeddingRun,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.evidenceEmbeddingRuns() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.evidenceEmbeddingSchedules() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.inboundQueueHealth() });
    },
  });
}
